import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2, RefreshCw, X } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  useProviderNotificationSettings,
  useUpdateProviderNotificationSettings,
} from '@/hooks/provider/useProviderNotificationSettings';
import {
  ProviderNotificationSettingsRequestError,
} from '@/services/provider/notification-settings.service';

type FormErrors = {
  bcc?: string;
  fromAddress?: string;
  fromName?: string;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_BCC = 10;
const MAX_EMAIL_LENGTH = 255;
const MAX_FROM_NAME_LENGTH = 255;

const normalizeEmail = (value: string): string => value.trim();
const normalizeText = (value: string): string => value.trim();

const resolveBackendError = (
  errors: Record<string, string[]> | undefined,
  prioritizedKeys: string[],
): string | null => {
  if (!errors) {
    return null;
  }

  for (const key of prioritizedKeys) {
    const message = errors[key]?.[0];
    if (message) {
      return message;
    }
  }

  return null;
};

export function ProviderNotificationSettingsCard() {
  const { t } = useTranslation();
  const settingsQuery = useProviderNotificationSettings();
  const updateMutation = useUpdateProviderNotificationSettings();

  const [bccEmails, setBccEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [fromAddress, setFromAddress] = useState('');
  const [fromName, setFromName] = useState('');
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (settingsQuery.data) {
      setBccEmails(settingsQuery.data.bccEmails ?? []);
      setFromAddress(settingsQuery.data.fromAddress ?? '');
      setFromName(settingsQuery.data.fromName ?? '');
      setInputValue('');
      setFormErrors({});
    }
  }, [settingsQuery.data]);

  const isSaving = updateMutation.isPending;

  const normalizedEmails = useMemo(() => {
    const unique = new Map<string, string>();
    for (const email of bccEmails) {
      const trimmed = normalizeEmail(email);
      if (trimmed.length === 0) {
        continue;
      }
      const key = trimmed.toLowerCase();
      if (!unique.has(key)) {
        unique.set(key, trimmed);
      }
    }
    return Array.from(unique.values());
  }, [bccEmails]);

  const validateForm = (
    emails: string[],
    normalizedFromAddress: string,
    normalizedFromName: string,
  ): FormErrors => {
    const next: FormErrors = {};

    if (normalizedFromName.length > MAX_FROM_NAME_LENGTH) {
      next.fromName = t(
        'settings.notificationSettings.validation.nameTooLong',
        'From name must be 255 characters or fewer.',
      );
    }

    if (normalizedFromAddress.length > MAX_EMAIL_LENGTH) {
      next.fromAddress = t(
        'settings.notificationSettings.validation.tooLong',
        'Email addresses must be 255 characters or fewer.',
      );
    } else if (normalizedFromAddress.length > 0 && !EMAIL_PATTERN.test(normalizedFromAddress)) {
      next.fromAddress = t(
        'settings.notificationSettings.validation.invalidEmail',
        'Enter a valid email address.',
      );
    } else if (normalizedFromName.length > 0 && normalizedFromAddress.length === 0) {
      next.fromAddress = t(
        'settings.notificationSettings.validation.fromRequired',
        'Enter a from email address.',
      );
    }

    if (emails.length > MAX_BCC) {
      next.bcc = t(
        'settings.notificationSettings.validation.tooMany',
        'You can add up to 10 BCC recipients.',
      );
      return next;
    }

    for (const email of emails) {
      if (email.length > MAX_EMAIL_LENGTH) {
        next.bcc = t(
          'settings.notificationSettings.validation.tooLong',
          'Email addresses must be 255 characters or fewer.',
        );
        break;
      }

      if (!EMAIL_PATTERN.test(email)) {
        next.bcc = t(
          'settings.notificationSettings.validation.invalidEmail',
          'Enter a valid email address.',
        );
        break;
      }
    }

    return next;
  };

  const addEmailsFromInput = (rawValue: string) => {
    const parts = rawValue
      .split(/[\s,]+/)
      .map((value) => normalizeEmail(value))
      .filter((value) => value.length > 0);

    if (parts.length === 0) {
      return;
    }

    const nextEmails = [...normalizedEmails];
    const existing = new Set(nextEmails.map((value) => value.toLowerCase()));

    for (const value of parts) {
      if (nextEmails.length >= MAX_BCC) {
        setFormErrors((prev) => ({
          ...prev,
          bcc: t(
            'settings.notificationSettings.validation.tooMany',
            'You can add up to 10 BCC recipients.',
          ),
        }));
        return;
      }

      if (value.length > MAX_EMAIL_LENGTH) {
        setFormErrors((prev) => ({
          ...prev,
          bcc: t(
            'settings.notificationSettings.validation.tooLong',
            'Email addresses must be 255 characters or fewer.',
          ),
        }));
        return;
      }

      if (!EMAIL_PATTERN.test(value)) {
        setFormErrors((prev) => ({
          ...prev,
          bcc: t(
            'settings.notificationSettings.validation.invalidEmail',
            'Enter a valid email address.',
          ),
        }));
        return;
      }

      const key = value.toLowerCase();
      if (!existing.has(key)) {
        nextEmails.push(value);
        existing.add(key);
      }
    }

    setBccEmails(nextEmails);
    setInputValue('');
    setFormErrors((prev) => ({ ...prev, bcc: undefined }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const normalizedFromAddress = normalizeEmail(fromAddress);
    const normalizedFromName = normalizeText(fromName);
    const nextErrors = validateForm(
      normalizedEmails,
      normalizedFromAddress,
      normalizedFromName,
    );
    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    try {
      const updated = await updateMutation.mutateAsync({
        bccEmails: normalizedEmails,
        fromAddress: normalizedFromAddress.length > 0 ? normalizedFromAddress : null,
        fromName:
          normalizedFromAddress.length > 0 && normalizedFromName.length > 0
            ? normalizedFromName
            : null,
      });
      setBccEmails(updated.bccEmails ?? []);
      setFromAddress(updated.fromAddress ?? '');
      setFromName(updated.fromName ?? '');
      setInputValue('');
      setFormErrors({});
      toast.success(
        t('settings.notificationSettings.saveSuccess', 'Notification settings saved.'),
      );
    } catch (error) {
      if (error instanceof ProviderNotificationSettingsRequestError) {
        const nextErrors: FormErrors = {};
        const bccMessage = resolveBackendError(error.errors, [
          'email.bcc',
          'email.bcc.0',
          'email.bcc.1',
        ]);
        const fromAddressMessage = resolveBackendError(error.errors, [
          'email.from.address',
          'email.from',
          'email',
        ]);
        const fromNameMessage = resolveBackendError(error.errors, [
          'email.from.name',
          'email.from',
          'email',
        ]);

        if (bccMessage) {
          nextErrors.bcc = bccMessage;
        }
        if (fromAddressMessage) {
          nextErrors.fromAddress = fromAddressMessage;
        }
        if (fromNameMessage) {
          nextErrors.fromName = fromNameMessage;
        }
        if (Object.keys(nextErrors).length > 0) {
          setFormErrors(nextErrors);
        }
      }
      const message =
        error instanceof Error && error.message
          ? error.message
          : t('settings.notificationSettings.saveError', 'Unable to save notification settings.');
      toast.error(message);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.includes(',') || value.includes(' ')) {
      addEmailsFromInput(value);
      return;
    }
    setInputValue(value);
  };

  if (settingsQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-muted-foreground" />
            {t('settings.notificationSettings.title', 'Email notifications')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.notificationSettings.description',
              'Configure sender details and BCC recipients for outgoing emails.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-32" />
        </CardContent>
      </Card>
    );
  }

  if (settingsQuery.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            {t('settings.notificationSettings.title', 'Email notifications')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.notificationSettings.description',
              'Configure sender details and BCC recipients for outgoing emails.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTitle>
              {t(
                'settings.notificationSettings.loadError',
                'Unable to load notification settings.',
              )}
            </AlertTitle>
            <AlertDescription className="mt-2 flex items-center gap-3">
              <span>{settingsQuery.error.message}</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => settingsQuery.refetch()}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                {t('common.retry', 'Retry')}
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-muted-foreground" />
          {t('settings.notificationSettings.title', 'Email notifications')}
        </CardTitle>
        <CardDescription>
          {t(
            'settings.notificationSettings.description',
            'Configure sender details and BCC recipients for outgoing emails.',
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-medium">
                {t('settings.notificationSettings.sender.title', 'Sender details')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(
                  'settings.notificationSettings.sender.helper',
                  'Shown in the From field for customer emails.',
                )}
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="from-name">
                  {t('settings.notificationSettings.fields.fromName', 'From name')}
                </label>
                <Input
                  id="from-name"
                  value={fromName}
                  placeholder={t(
                    'settings.notificationSettings.placeholders.fromName',
                    'Acme',
                  )}
                  onChange={(event) => setFromName(event.target.value)}
                  disabled={isSaving}
                />
                {formErrors.fromName ? (
                  <p className="text-sm text-destructive">{formErrors.fromName}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="from-address">
                  {t('settings.notificationSettings.fields.fromAddress', 'From email')}
                </label>
                <Input
                  id="from-address"
                  type="email"
                  value={fromAddress}
                  placeholder={t(
                    'settings.notificationSettings.placeholders.fromAddress',
                    'no-reply@acme.com',
                  )}
                  onChange={(event) => setFromAddress(event.target.value)}
                  disabled={isSaving}
                />
                {formErrors.fromAddress ? (
                  <p className="text-sm text-destructive">{formErrors.fromAddress}</p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <label className="text-sm font-medium" htmlFor="bcc-input">
                {t('settings.notificationSettings.fields.bcc', 'BCC recipients')}
              </label>
              <span className="text-xs text-muted-foreground">
                {t('settings.notificationSettings.count', {
                  count: normalizedEmails.length,
                  max: MAX_BCC,
                })}
              </span>
            </div>
            <div className={cn('flex flex-wrap gap-2 rounded-md border p-2', formErrors.bcc && 'border-destructive')}>
              {normalizedEmails.length === 0 ? (
                <span className="text-xs text-muted-foreground">
                  {t(
                    'settings.notificationSettings.empty',
                    'No BCC recipients added yet.',
                  )}
                </span>
              ) : null}
              {normalizedEmails.map((email) => (
                <Badge key={email} variant="secondary" className="gap-1">
                  {email}
                  <button
                    type="button"
                    onClick={() =>
                      setBccEmails((prev) => prev.filter((value) => value !== email))
                    }
                    className="ml-1 rounded-sm hover:bg-secondary/80"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <Input
                id="bcc-input"
                type="email"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={(event) => {
                  if ((event.key === 'Enter' || event.key === ',') && inputValue.trim()) {
                    event.preventDefault();
                    addEmailsFromInput(inputValue);
                  } else if (event.key === 'Backspace' && inputValue.length === 0 && normalizedEmails.length > 0) {
                    setBccEmails((prev) => prev.slice(0, -1));
                  }
                }}
                placeholder={t(
                  'settings.notificationSettings.placeholders.bcc',
                  'audit@example.com',
                )}
                className="min-w-[200px] flex-1 border-0 bg-transparent px-0 focus-visible:ring-0"
                disabled={isSaving}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {t(
                'settings.notificationSettings.helper',
                'Add up to 10 BCC recipients for OTPs, invitations, verification, and billing emails.',
              )}
            </p>
            <p className="text-xs text-muted-foreground">
              {t(
                'settings.notificationSettings.hint',
                'Press Enter or comma to add another address.',
              )}
            </p>
            {formErrors.bcc ? (
              <p className="text-sm text-destructive">{formErrors.bcc}</p>
            ) : null}
          </div>

          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setBccEmails([]);
                setInputValue('');
                setFromAddress('');
                setFromName('');
                setFormErrors({});
              }}
              disabled={isSaving}
            >
              {t('common.clear', 'Clear')}
            </Button>
            <Button type="submit" disabled={isSaving} className="gap-2">
              {isSaving && <RefreshCw className="h-4 w-4 animate-spin" />}
              {t('settings.notificationSettings.actions.save', 'Save BCC list')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
